import { lazy } from 'react';

const RELOAD_MARKER = 'chunk-reload-at';
const RELOAD_COOLDOWN_MS = 15_000;

/** sessionStorage throws in some privacy modes; never let that break the page. */
function lastReloadAt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARKER) || 0);
  } catch {
    return 0;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch {
    // Ignore: worst case the page reloads again on the next failure.
  }
}

/**
 * React.lazy that survives a deploy.
 *
 * Vite gives every build hashed file names. A tab opened before a deploy still asks for the old
 * names, which no longer exist, so the SPA fallback returns index.html and the import fails with a
 * MIME type error and a blank screen. When that happens, reload once to pick up the new build.
 * The cooldown stops a reload loop if the import is failing for some other reason (offline, etc).
 */
export function lazyWithReload<T extends Parameters<typeof lazy>[0]>(factory: T) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (Date.now() - lastReloadAt() > RELOAD_COOLDOWN_MS) {
        markReloaded();
        window.location.reload();
      }
      throw error;
    }
  });
}
